import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Download, Search, Loader2, RefreshCw, Phone, Mail,
  Calendar, FileText, ChevronDown, Check, CreditCard, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: "new", label: "신규", color: "bg-burgundy-100 text-burgundy-700 border-burgundy-200" },
  { value: "contacted", label: "연락 완료", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "completed", label: "상담 완료", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "rejected", label: "거절", color: "bg-stone-100 text-stone-500 border-stone-200" },
];

const TABS = [
  { value: "all", label: "전체" },
  { value: "new", label: "신규" },
  { value: "contacted", label: "연락 완료" },
  { value: "completed", label: "상담 완료" },
  { value: "rejected", label: "거절" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return iso; }
};

const StatusBadge = ({ value }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === value) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${opt.color}`}>
      {opt.label}
    </span>
  );
};

const StatusDropdown = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block" onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-stone-200 bg-white hover:bg-stone-50 disabled:opacity-50"
        data-testid={`status-dropdown-${value}`}
      >
        <StatusBadge value={value} />
        <ChevronDown className="w-3 h-3 text-stone-400" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-20 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[140px]">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setOpen(false); onChange(opt.value); }}
              className="w-full flex items-center justify-between gap-2 text-xs px-3 py-1.5 hover:bg-stone-50 text-left"
              data-testid={`status-option-${opt.value}`}
            >
              <span className="flex items-center gap-2">
                <StatusBadge value={opt.value} />
              </span>
              {opt.value === value && <Check className="w-3 h-3 text-burgundy-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminDatingAgencyPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (activeTab !== "all") params.set("status", activeTab);
      if (q.trim()) params.set("q", q.trim());
      const res = await safeFetch(`${API_URL}/api/admin/dating-agency/inquiries?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "목록을 불러오지 못했습니다.");
      setItems(data.items || []);
      setTotal(data.total || 0);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e.message || "목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, q]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") {
      toast.error("관리자만 접근 가능합니다.");
      navigate("/");
      return;
    }
    fetchList();
  }, [authLoading, user, navigate, fetchList]);

  const updateStatus = async (id, newStatus) => {
    try {
      const res = await safeFetch(`${API_URL}/api/admin/dating-agency/inquiries/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "상태 변경 실패");
      toast.success(`상태가 '${STATUS_OPTIONS.find((s) => s.value === newStatus)?.label}'로 변경됐어요.`);
      // optimistic update
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: newStatus } : it)));
      // refresh counts
      fetchList();
    } catch (e) {
      toast.error(e.message || "상태 변경 실패");
    }
  };

  const updateStep = async (id, newStep) => {
    try {
      const res = await safeFetch(`${API_URL}/api/admin/dating-agency/inquiries/${id}/step`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ process_step: newStep }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "단계 변경 실패");
      toast.success(`진행 단계가 ${newStep}단계로 변경됐어요.`);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, process_step: newStep } : it)));
      fetchList();
    } catch (e) {
      toast.error(e.message || "단계 변경 실패");
    }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await safeFetch(`${API_URL}/api/admin/dating-agency/inquiries/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("내보내기 실패");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dating-agency-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV 다운로드를 시작했어요.");
    } catch (e) {
      toast.error(e.message || "내보내기 실패");
    } finally {
      setExporting(false);
    }
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-burgundy-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="admin-dating-agency-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">연애정보회사 상담 신청</h1>
              <p className="text-stone-500 text-sm mt-0.5">엘리트 · 하이앤드 · 노블 · 로얄 플랜 신청자 관리 · 총 {counts.all || 0}건</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchList} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> 새로고침
            </Button>
            <Button size="sm" onClick={exportCSV} disabled={exporting} data-testid="export-csv-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              CSV 내보내기
            </Button>
          </div>
        </div>

        {/* Tabs + Search */}
        <div className="bg-white rounded-xl border border-stone-200 mb-4">
          <div className="flex items-center border-b border-stone-100 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  activeTab === tab.value
                    ? "border-burgundy-600 text-burgundy-700"
                    : "border-transparent text-stone-500 hover:text-stone-700"
                }`}
                data-testid={`tab-${tab.value}`}
              >
                {tab.label}
                <span className="ml-1.5 text-xs text-stone-400">({counts[tab.value] || 0})</span>
              </button>
            ))}
          </div>
          <div className="px-4 py-3 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchList()}
                placeholder="이름·이메일·전화번호 검색"
                className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                data-testid="search-input"
              />
            </div>
            <Button size="sm" variant="outline" onClick={fetchList}>검색</Button>
            {q && <Button size="sm" variant="ghost" onClick={() => { setQ(""); fetchList(); }}>초기화</Button>}
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-burgundy-600 mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center text-stone-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-stone-300" />
              <p>아직 신청 내역이 없습니다.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr className="text-left text-stone-600">
                    <th className="px-4 py-3 font-medium">신청자</th>
                    <th className="px-4 py-3 font-medium">연락처</th>
                    <th className="px-4 py-3 font-medium">플랜 · 결제</th>
                    <th className="px-4 py-3 font-medium">문의 내용</th>
                    <th className="px-4 py-3 font-medium">진행 단계</th>
                    <th className="px-4 py-3 font-medium">신청일</th>
                    <th className="px-4 py-3 font-medium">상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {items.map((it) => (
                    <tr key={it.id} className="hover:bg-stone-50/60" data-testid={`inquiry-row-${it.id}`}>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-stone-900">{it.name}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-stone-600">
                        <div className="flex items-center gap-1.5">
                          <Phone className="w-3 h-3 text-stone-400" />
                          <a href={`tel:${it.phone}`} className="hover:text-burgundy-600">{it.phone}</a>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Mail className="w-3 h-3 text-stone-400" />
                          <a href={`mailto:${it.email}`} className="hover:text-burgundy-600 break-all">{it.email}</a>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-stone-700">
                        <div className="font-medium">{it.plan_name}</div>
                        <div className="text-xs text-stone-500 mt-0.5">USD ${it.plan_price?.toLocaleString()} / {it.plan_duration}</div>
                        <div className="mt-1.5">
                          {it.payment_status === "paid" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                              <CreditCard className="w-3 h-3" />결제완료
                            </span>
                          ) : it.payment_status === "pending" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                              결제대기
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-stone-100 text-stone-500 border border-stone-200">
                              미결제
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-stone-600 max-w-xs">
                        {it.message ? (
                          <>
                            <div className="line-clamp-3 whitespace-pre-wrap">{it.message}</div>
                            {(it.message.length > 60 || it.message.includes("\n")) && (
                              <button
                                onClick={() => setDetailItem(it)}
                                className="mt-1 text-xs font-semibold text-burgundy-600 hover:text-burgundy-700 hover:underline"
                                data-testid={`inquiry-more-${it.id}`}
                              >
                                더보기
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-stone-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {it.payment_status === "paid" ? (
                          <div className="flex items-center gap-1.5" data-testid={`step-cell-${it.id}`}>
                            <span className="text-sm font-bold text-burgundy-700">{it.process_step || 1}/6</span>
                            <select
                              value={it.process_step || 1}
                              onChange={(e) => updateStep(it.id, parseInt(e.target.value, 10))}
                              className="text-xs px-1.5 py-1 border border-stone-200 rounded bg-white"
                              data-testid={`step-select-${it.id}`}
                            >
                              <option value={1}>1. 계약서 작성</option>
                              <option value={2}>2. 자기소개서</option>
                              <option value={3}>3. 혼인관계증명서</option>
                              <option value={4}>4. 커플매니저 배정</option>
                              <option value={5}>5. 줌 인터뷰</option>
                              <option value={6}>6. 소개팅 진행</option>
                            </select>
                          </div>
                        ) : (
                          <span className="text-xs text-stone-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-stone-500 whitespace-nowrap">
                        <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{formatDate(it.created_at)}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <StatusDropdown value={it.status} onChange={(v) => updateStatus(it.id, v)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && items.length > 0 && (
          <p className="text-xs text-stone-400 text-center mt-4">총 {total}건 중 {items.length}건 표시</p>
        )}
      </div>

      {/* 상담 신청 상세 모달 */}
      {detailItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDetailItem(null)}
          data-testid="inquiry-detail-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="inquiry-detail-modal"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100 sticky top-0 bg-white rounded-t-2xl">
              <div>
                <h3 className="text-lg font-bold text-stone-900">{detailItem.name} 님의 상담 신청</h3>
                <p className="text-xs text-stone-400 mt-0.5">{formatDate(detailItem.created_at)}</p>
              </div>
              <button
                onClick={() => setDetailItem(null)}
                className="p-1.5 rounded-full hover:bg-stone-100 text-stone-500"
                data-testid="inquiry-detail-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-stone-400 mb-1">연락처</p>
                  <a href={`tel:${detailItem.phone}`} className="flex items-center gap-1.5 text-stone-700 hover:text-burgundy-600"><Phone className="w-3.5 h-3.5 text-stone-400" />{detailItem.phone}</a>
                  <a href={`mailto:${detailItem.email}`} className="flex items-center gap-1.5 text-stone-700 hover:text-burgundy-600 mt-1 break-all"><Mail className="w-3.5 h-3.5 text-stone-400" />{detailItem.email}</a>
                </div>
                <div>
                  <p className="text-xs text-stone-400 mb-1">플랜 · 결제</p>
                  <p className="font-medium text-stone-800">{detailItem.plan_name}</p>
                  <p className="text-xs text-stone-500">USD ${detailItem.plan_price?.toLocaleString()} / {detailItem.plan_duration}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-stone-400 mb-1.5">문의 내용</p>
                <div className="bg-stone-50 border border-stone-100 rounded-xl p-4 whitespace-pre-wrap leading-relaxed text-stone-700" data-testid="inquiry-detail-message">
                  {detailItem.message || <span className="text-stone-400">문의 내용 없음</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
